export type ProposedArticleStatus = 'proposed' | 'saved' | 'published' | 'dismissed';

export interface ProposedArticleSource {
  url: string;
  title?: string;
  publishedAt?: string;
  domain?: string;
}

export interface ProposedArticle {
  tenantId: string;
  brandId: string;
  articleId: string;
  title: string;
  proposedAngle: string;
  newsDigest: string;
  sourceUrls: ProposedArticleSource[];
  topicTags: string[];
  competitorMentions?: string[];
  sourceAuditId: string;
  /** Provenance label when seeded from an audit recommendation (reco mode),
   *  e.g. "Création de contenu · Fraîcheur". Absent on organic News Watch runs. */
  originLabel?: string;
  status: ProposedArticleStatus;
  contentHash: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  publishedUrl?: string;
  /**
   * News Watch v2 (2026-06): the full generated article + channel variants.
   * Optional — pre-v2 rows have no `article` field and render via the legacy
   * title/proposedAngle/newsDigest path. When present, the editor UI reads this.
   */
  article?: GeneratedArticle;
}

// ============================================================
// News Watch v2 — content creation (Studio de contenu)
// ============================================================

/**
 * Editorial length RELATIVE to the channel — a "court" LinkedIn post ≈ 1-2
 * sentences, a "court" blog article ≈ 300 words. Never an absolute word count.
 */
export type ContentFormat = 'micro' | 'court' | 'standard' | 'long';

/** A distribution channel the user selected at creation. The article + its
 *  variants are generated only for the chosen channels. Run-path ids. */
export type ContentChannel =
  | 'blog_seo'
  | 'landing_page'
  | 'medium'
  | 'linkedin_post'
  | 'linkedin_article'
  | 'x_thread'
  | 'x_post'
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'threads'
  | 'reddit'
  | 'quora'
  | 'newsletter';

/** Optional editorial pattern(s) — multi-select. */
export type ContentPattern = 'storytelling' | 'listicle' | 'guide' | 'opinion';

/** Business objective — single-select. Shapes tone + CTA. */
export type ContentObjective = 'acquisition' | 'authority' | 'conversion' | 'image';

/** How the topic is sourced (left pane of the hub).
 *  `catalog` (Product module, Phase 6): ground the content in real products from
 *  one or more retailer catalogues, scoped by a free-text brief. */
export type ContentSourceMode = 'propose' | 'provide' | 'reco' | 'catalog';

/** Seed input limits per source mode (enforced frontend + backend). `provide`
 *  allows a full pasted article/brief; `propose` is just a short orientation. */
export const CONTENT_SEED_LIMITS: Record<ContentSourceMode, number> = {
  propose: 1_000,
  provide: 30_000,
  // Matches the ContentTopic `topic` cap (createContentTopic slices to 500). A
  // content-target reco carries `title — detail` (title ≤160 + detail ≤600), so a
  // 300 cap truncated the brief ("que doit couvrir ce contenu") on generate.
  reco: 500,
  catalog: 1_000,
};

/** Editorial angle for catalog-sourced product content. Shapes structure + tone. */
export type ProductContentAngle = 'guide' | 'benchmark' | 'story' | 'promo';

/** A product surfaced by the catalogue vector search, grounding the article. */
export interface GroundedProduct {
  productId: string;
  /** Raw catalogue name — often unfriendly (e.g. "AAAA Plancha - LIVOO - DOC292").
   *  Kept for identity; NOT necessarily what the article should display. */
  name: string;
  url: string;
  /** A reader-friendly label for the link anchor / mention (derived from the
   *  optimized wording USP or a cleaned name). The agent prefers this over `name`
   *  and may still reword it in the running prose. */
  displayName?: string;
}

/** `provide` mode must carry enough material to rewrite from: either a valid URL
 *  (the agent fetches it) OR at least this many words of pasted content/angle. */
export const CONTENT_PROVIDE_MIN_WORDS = 10;

/** Count words in a free-text seed (Unicode-aware, matches the agent). */
export function seedWordCount(text: string): number {
  return (text || '').match(/[\p{L}\p{N}]+/gu)?.length ?? 0;
}

/** True when the seed is (or contains) a valid http(s) URL. */
export function seedHasUrl(text: string): boolean {
  return /https?:\/\/[^\s]+\.[^\s]{2,}/i.test(text || '');
}

/** Whether a `provide`-mode seed carries enough to generate: a URL or ≥ min words. */
export function isProvideSeedSufficient(text: string): boolean {
  const t = (text || '').trim();
  return seedHasUrl(t) || seedWordCount(t) >= CONTENT_PROVIDE_MIN_WORDS;
}

/**
 * The creation config chosen in the hub (left pane). Threaded through the audit
 * `config.contentCreation` → Trigger Lambda → agent payload. All fields have
 * sane defaults on the agent side so Phase B works before the Phase D UI ships.
 */
export interface ContentCreationConfig {
  sourceMode: ContentSourceMode;
  /** For `provide`: the user's text / angle / brief / URL. For `propose`: an optional seed. */
  seed?: string;
  /** For `reco`: the picked recommended topic (title) + its origin. */
  recoTopic?: string;
  /** Provenance — when created from an audit recommendation, the audit id + a
   *  human label (e.g. "Contenu · Fraîcheur") so the Studio card can show origin. */
  sourceAuditId?: string;
  originLabel?: string;
  format: ContentFormat;
  channels: ContentChannel[];
  patterns: ContentPattern[];
  objective: ContentObjective;
  // --- catalog source mode (Phase 6) ---
  /** Catalogue(s) to ground the content in (retailer Product module). */
  catalogueIds?: string[];
  /** Editorial angle for catalog content. */
  productAngle?: ProductContentAngle;
  /** Products the API resolved from the scope brief (vector search) at dispatch —
   *  the agent grounds the article in these (names + real URLs). */
  groundedProducts?: GroundedProduct[];
}

/** One generated social/channel variant. */
export interface SocialVariant {
  /** `<channel>_<kind>_<index>` — stable id for regenerate/copy. */
  variantId: string;
  channel: ContentChannel;
  /** Variant flavor within a channel (announcement / carousel / thread / post / caption…). */
  kind: string;
  /** 1-based within (channel, kind). */
  index: number;
  /** Single-string variants (post, caption, tweet, blurb). */
  text?: string;
  /** Structured variants (carousel slides, thread tweets) — typed loosely for storage. */
  structured?: unknown;
  charCount: number;
}

/** A citability check result for the GEO sidebar. `tier` distinguishes a
 *  deterministically-VERIFIED pass (green) from an AI-SUGGESTED one (grey). */
export interface CitabilityCheck {
  id: string;
  label: string;
  passed: boolean;
  tier: 'verified' | 'suggested';
  detail?: string;
  /** Human impact label from the GEO knowledge base, e.g. "+40%", "+++". */
  impact?: string;
}

/** An entity the article should mention, with deterministic presence flag. */
export interface ArticleEntity {
  name: string;
  present: boolean;
}

/** A source surfaced during harvest, with whether it was actually cited in the body. */
export interface ArticleSource {
  url: string;
  domain: string;
  /** web | news | scholar — which Serper endpoint surfaced it. */
  kind?: 'web' | 'news' | 'scholar';
  cited: boolean;
  why?: string;
}

/**
 * A cached GEO/best-practice evaluation for one target (the article, or a
 * channel like `linkedin_post`). Deterministic checks are always fresh; the LLM
 * judgment checks may be `partial` if the model call failed (→ ↻ retry).
 */
export interface TargetEvaluation {
  target: string; // 'article' | ContentChannel
  checks: CitabilityCheck[];
  /** true when the LLM judgment half failed → checklist shows deterministic-only. */
  partial: boolean;
  evaluatedAt: string;
}

/** Per-article evaluation state: cached results + throttle counters. */
export interface EvaluationState {
  /** Keyed by target ('article' | channel). */
  byTarget: Record<string, TargetEvaluation>;
  /** Total manual regenerations used (hard cap enforced server-side). */
  regenCount: number;
  /** ISO of the last manual regeneration (1/min throttle). */
  lastRegenAt?: string;
}

/** Manual regeneration limits (free feature — bound abuse). */
export const EVAL_REGEN_MAX = 10;
export const EVAL_REGEN_COOLDOWN_MS = 60_000;

/** The full generated article + all channel variants + GEO metadata. */
export interface GeneratedArticle {
  title: string;
  subtitle?: string;
  /** Canonical publishable content (full markdown). */
  articleMarkdown: string;
  /** The AI-search query this article is built to be cited for. */
  targetGeoQuery: string;
  citabilityChecklist: CitabilityCheck[];
  entities: ArticleEntity[];
  sources: ArticleSource[];
  wordCount: number;
  /** 0-100 French readability (Kandel-Moles Flesch adaptation). */
  readability?: number;
  /** News velocity band from the /news endpoint (0 = evergreen). */
  momentum?: 'forte' | 'moyenne' | 'faible' | 'evergreen';
  newsDigest: string;
  socialVariants: SocialVariant[];
  /** The creation config this was generated from. */
  config: ContentCreationConfig;
  generatedAt: string;
  generatedBy: { article: string; variants: string };
  /** Cached GEO/best-practice evaluations per target (lazy — computed on first
   *  editor open, refreshed via the ↻ regenerate icon after manual edits). */
  evaluation?: EvaluationState;
}

/**
 * A saved content idea ("sujet de contenu"), the persisted form of a
 * content-type audit recommendation the user chose to act on. It lives in the
 * Studio de création as a pickable topic (source mode `reco`) rather than
 * firing a generation immediately — the user selects it in section 1 and
 * generates when ready. Stored on the proposed-articles table under a distinct
 * SK prefix (`CONTENT_TOPIC#`).
 */
export interface ContentTopic {
  tenantId: string;
  brandId: string;
  topicId: string;
  /** The idea, ready to feed the generator (title + optional detail). */
  topic: string;
  /** Human origin label, e.g. "Création de contenu · Fraîcheur". */
  originLabel?: string;
  /** The audit this idea came from. */
  sourceAuditId?: string;
  /** When the idea came from a product-measurement result, the catalogue it
   *  concerns + the real product(s) to ground the article in (verified URLs). So
   *  reco-mode generation grounds like catalog-mode instead of inventing URLs. */
  catalogueId?: string;
  groundedProducts?: GroundedProduct[];
  /** 'pending' = not yet turned into an article; 'used' = generated at least once. */
  status: 'pending' | 'used';
  createdAt: string;
  updatedAt: string;
}

export interface CreateContentTopicInput {
  topic: string;
  originLabel?: string;
  sourceAuditId?: string;
  catalogueId?: string;
  groundedProducts?: GroundedProduct[];
}

export interface NewsTagAffinity {
  tenantId: string;
  brandId: string;
  tag: string;
  savedCount: number;
  lastSavedAt: string;
}

export interface NewsKeywordSuggestion {
  tag: string;
  savedCount: number;
  lastSavedAt: string;
}
